// Import the RTK Query methods from the React-specific entry point
import { createApi } from "@reduxjs/toolkit/query/react";
import { adsBaseQuery } from "./customBaseQuery";
import {
  AdditionalCheckoutField,
  AdPlatformAccount,
  AdPlatformCampaign,
  AffiliateCode,
  AnalyticsElement,
  AnalyticsElementType,
  AudienceSegment,
  Brand,
  Campaign,
  City,
  Contest,
  ContestDiscountCode,
  ContestEntry,
  DomainConfig,
  EventListFilterTypes,
  Game,
  Label,
  LocationResult,
  MailingList,
  MailingListSummary,
  Menu,
  MenuItem,
  Page,
  Participant,
  PassesInfo,
  PaymentDestination,
  Permission,
  Prize,
  Product,
  ProductAvailableForSaleSubscription,
  ProductAvailableForSaleSubscriptionSummaryRow,
  ProductCategory,
  ProductContentBlock,
  PurchasedGiftCard,
  ReferenceData,
  ReferralLink,
  Review,
  ReviewAnalyzerResults,
  SeatAllocation,
  SeatPrice,
  Subscriber,
  Subscription,
  SummaryType,
  Template,
  Ticket,
  TicketSummary,
  Venue,
  Widget,
} from "../models/models";
import { MetaCity, MetaCountry, MetaInterest } from "@models/ad-campaigns";
import _ from "lodash";
import { PaginationRequest, PaginationResponse } from "@models/pagination";
import { ReviewsFilterParams } from "@hooks/reviews";
import { MailingListSubscriberValues } from "@components/mailing-list-sign-up";
import { NotifyWhenProductAvailableValues } from "@components/notify-when-product-available-form";
import { CreateLinkValues } from "@app/[locale]/admin/(default)/events/[productId]/draft-orders/page";

// Define our single API slice object
export const adsSlice = createApi({
  // The cache reducer expects to be added at `state.api` (already default - this is optional)
  reducerPath: "adsApi",
  baseQuery: adsBaseQuery,
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    getAdPlatformAccounts: builder.query<AdPlatformAccount[], string>({
      query: (accountId) => `/api/accounts/${accountId}/ad-platform-accounts`,
      transformResponse: (response: { data: AdPlatformAccount[] }) =>
        response.data,
    }),
    getAdCampaigns: builder.query<AdPlatformCampaign[], string>({
      query: (accountId) => `/api/accounts/${accountId}/ad-campaigns`,
      transformResponse: (response: { data: AdPlatformCampaign[] }) =>
        response.data,
    }),
    getMetaCountries: builder.query<ReferenceData[], void>({
      query: () => `/api/references/meta/countries`,
      transformResponse: (response: { data: ReferenceData[] }) => response.data,
    }),
    getMetaCampaign: builder.query<any, string>({
      query: (ad_platform_campaign_id) =>
        `/api/meta/campaigns/${ad_platform_campaign_id}`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getMetaAdSet: builder.query<
      any,
      {
        ad_platform_account_id?: string;
        meta_ad_set_id: string;
        ad_platform_campaign_id?: string;
      }
    >({
      query: (payload) => ({
        url: `/api/meta/ad-sets/${payload.meta_ad_set_id}`,
        params: {
          ad_platform_account_id: payload.ad_platform_account_id,
          ad_platform_campaign_id: payload.ad_platform_campaign_id,
        },
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    getMetaBudgetTypes: builder.query<ReferenceData[], void>({
      query: () => `/api/references/meta/budget-types`,
      transformResponse: (response: { data: ReferenceData[] }) => response.data,
    }),
    getMetaCampaignObjectives: builder.query<ReferenceData[], void>({
      query: () => `/api/references/meta/campaign-objectives`,
      transformResponse: (response: { data: ReferenceData[] }) => response.data,
    }),
    getCallToActionTypes: builder.query<ReferenceData[], void>({
      query: () => `/api/references/meta/call-to-action-types`,
      transformResponse: (response: { data: ReferenceData[] }) => response.data,
    }),
    getMetaCustomAudiences: builder.query<any[], string>({
      query: (ad_platform_account_id) =>
        `/api/meta/custom-audiences?ad_platform_account_id=${ad_platform_account_id}`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getMetaAdCreatives: builder.query<any, string>({
      query: (ad_platform_account_id) =>
        `/api/meta/ad-creatives?ad_platform_account_id=${ad_platform_account_id}`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getMetaPages: builder.query<any, string>({
      query: (ad_platform_account_id) =>
        `/api/meta/pages?ad_platform_account_id=${ad_platform_account_id}`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getMetaAdPixels: builder.query<any, string>({
      query: (ad_platform_account_id) =>
        `/api/meta/ad-pixels?ad_platform_account_id=${ad_platform_account_id}`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getMetaCampaignAdSets: builder.query<any, string>({
      query: (ad_platform_campaign_id) =>
        `/api/meta/campaigns/${ad_platform_campaign_id}/ad-sets`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getMetaAdsInAdSets: builder.query<
      any,
      { ad_platform_campaign_id: string | number; meta_ad_set_id: string }
    >({
      query: (payload) =>
        `/api/meta/campaigns/${payload.ad_platform_campaign_id}/ad-sets/${payload.meta_ad_set_id}/ads`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getMetaAdPreview: builder.query<
      any,
      { ad_platform_account_id: string; meta_ad_id: string; ad_format: string }
    >({
      query: (payload) =>
        `/api/meta/ads/${payload.meta_ad_id}/preview?ad_format=${payload.ad_format}&ad_platform_account_id=${payload.ad_platform_account_id}`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getMetaAdCreativePreview: builder.query<
      any,
      {
        ad_platform_account_id: string;
        meta_ad_creative_id: string;
        ad_format: string;
      }
    >({
      query: (payload) =>
        `/api/meta/ad-creatives/${payload.meta_ad_creative_id}/preview?ad_format=${payload.ad_format}&ad_platform_account_id=${payload.ad_platform_account_id}`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getMetaObjectStorySpecPreview: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/api/meta/object-story-spec-preview`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    searchMetaCities: builder.query<
      MetaCity[],
      { ad_platform_account_id: string; search_string: string }
    >({
      query: (payload) =>
        `/api/meta/search/cities?ad_platform_account_id=${payload.ad_platform_account_id}&query=${payload.search_string}`,
      transformResponse: (response: { data: MetaCity[] }) => response.data,
    }),
    cloneEmailCampaign: builder.mutation<{ status: string }, string>({
      query: (campaign_id) => ({
        url: `/api/campaigns/${campaign_id}/clone`,
        method: "POST",
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    searchMetaCountries: builder.query<
      MetaCountry[],
      { ad_platform_account_id: string; search_string: string }
    >({
      query: (payload) =>
        `/api/meta/search/countries?ad_platform_account_id=${payload.ad_platform_account_id}&query=${payload.search_string}`,
      transformResponse: (response: { data: MetaCountry[] }) => response.data,
    }),
    searchMetaInterests: builder.query<
      MetaInterest[],
      { ad_platform_account_id: string; search_string: string }
    >({
      query: (payload) =>
        `/api/meta/search/interests?ad_platform_account_id=${payload.ad_platform_account_id}&query=${payload.search_string}`,
      transformResponse: (response: { data: MetaInterest[] }) => response.data,
    }),
    getAdPlatformAccount: builder.query<AdPlatformAccount, string>({
      query: (id) => `/api/ad-platform-accounts/${id}`,
      transformResponse: (response: { data: AdPlatformAccount }) =>
        response.data,
    }),
    getAdPlatformCampaign: builder.query<AdPlatformCampaign, string>({
      query: (id) => `/api/ad-platform-campaigns/${id}`,
      transformResponse: (response: { data: AdPlatformCampaign }) =>
        response.data,
    }),
    updateMetaAdAccounts: builder.mutation<void, any>({
      query: (payload) => ({
        url: `/api/meta/update-ad-accounts`,
        method: "POST",
        body: payload,
      }),
    }),
    createCustomAudience: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/api/meta/custom-audiences`,
        method: "POST",
        body: payload,
      }),
    }),
    createMetaAdCreative: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/api/meta/ad-creatives`,
        method: "POST",
        body: payload,
      }),
    }),
    createMetaCampaign: builder.mutation<number, any>({
      query: (payload) => ({
        url: `/api/meta/campaigns`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: number }) => response.data,
    }),
    createMetaAdSet: builder.mutation<number, any>({
      query: (payload) => ({
        url: `/api/meta/campaigns/${payload.ad_platform_campaign_id}/ad-sets`,
        method: "POST",
        body: {
          ...payload,
          ad_platform_campaign_id: undefined,
          budget_amount: Number(payload.budget_amount),
          bid_amount: Number(payload.bid_amount),
        },
      }),
      transformResponse: (response: { data: number }) => response.data,
    }),
    createMetaAd: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/api/meta/campaigns/${payload.ad_platform_campaign_id}/ad-sets/${payload.meta_ad_set_id}/ads`,
        method: "POST",
        body: { meta_ad_creative_id: payload.meta_ad_creative_id },
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    deleteAdCreative: builder.mutation<
      any,
      { ad_platform_account_id: string; meta_ad_creative_id: string }
    >({
      query: (payload) => ({
        url: `/api/meta/ad-creatives/${payload.meta_ad_creative_id}?ad_platform_account_id=${payload.ad_platform_account_id}`,
        method: "DELETE",
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    deleteMetaCampaign: builder.mutation<
      any,
      { ad_platform_account_id: string; meta_campaign_id: string }
    >({
      query: (payload) => ({
        url: `/api/meta/campaigns/${payload.meta_campaign_id}?ad_platform_account_id=${payload.ad_platform_account_id}`,
        method: "DELETE",
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    deleteMetaAd: builder.mutation<
      any,
      { ad_platform_account_id: string; meta_ad_id: string }
    >({
      query: (payload) => ({
        url: `/api/meta/ads/${payload.meta_ad_id}?ad_platform_account_id=${payload.ad_platform_account_id}`,
        method: "DELETE",
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    updateMetaAdSet: builder.mutation<
      any,
      {
        field: string;
        meta_ad_set_id: string;
        ad_platform_account_id: string;
        value: any;
      }
    >({
      query: (payload) => ({
        url: `/api/meta/ad-sets/${payload.meta_ad_set_id}?ad_platform_account_id=${payload.ad_platform_account_id}`,
        method: "POST",
        body: { field: payload.field, value: payload.value },
      }),
    }),
    updateMetaCampaign: builder.mutation<
      any,
      {
        field: string;
        meta_campaign_id: string;
        ad_platform_account_id: string;
        value: any;
      }
    >({
      query: (payload) => ({
        url: `/api/meta/campaigns/${payload.meta_campaign_id}?ad_platform_account_id=${payload.ad_platform_account_id}`,
        method: "POST",
        body: { field: payload.field, value: payload.value },
      }),
    }),
    cloneMetaCampaign: builder.mutation<
      any,
      {
        meta_campaign_id: string;
        ad_platform_account_id: string;
        name: string;
      }
    >({
      query: (payload) => ({
        url: `/api/meta/campaigns/${payload.meta_campaign_id}/clone?ad_platform_account_id=${payload.ad_platform_account_id}`,
        method: "POST",
        body: { name: payload.name },
      }),
    }),
    updateMetaAd: builder.mutation<
      any,
      {
        field: string;
        meta_ad_id: string;
        ad_platform_account_id: string;
        value: any;
      }
    >({
      query: (payload) => ({
        url: `/api/meta/ads/${payload.meta_ad_id}?ad_platform_account_id=${payload.ad_platform_account_id}`,
        method: "POST",
        body: { field: payload.field, value: payload.value },
      }),
    }),
    getInstagramActorIdsByPageId: builder.query<
      { id: number }[],
      {
        meta_page_id: string;
        ad_platform_account_id: string;
      }
    >({
      query: (payload) =>
        `/api/meta/pages/${payload.meta_page_id}/instagram-actor-ids?ad_platform_account_id=${payload.ad_platform_account_id}`,
      transformResponse: (response: { data: { id: number }[] }) =>
        response.data,
    }),
    createInstagramActorIdsByPageId: builder.mutation<
      any,
      {
        meta_page_id: string;
        ad_platform_account_id: string;
      }
    >({
      query: (payload) => ({
        url: `/api/meta/pages/${payload.meta_page_id}/instagram-actor-ids?ad_platform_account_id=${payload.ad_platform_account_id}`,
        method: "POST",
      }),
    }),
    getSignedCustomAudienceTos: builder.query<string, string>({
      query: (ad_platform_account_id) =>
        `/api/meta/signed-custom-audience-tos?ad_platform_account_id=${ad_platform_account_id}`,
      transformResponse: (response: { data: string }) => response.data,
    }),
    getAccountAnalyticsElements: builder.query<AnalyticsElement[], string>({
      query: (account_id) => `/api/accounts/${account_id}/analytics-elements`,
      transformResponse: (response: { data: AnalyticsElement[] }) =>
        response.data,
    }),
    createAccountAnalyticsElement: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/api/analytics-elements`,
        method: "POST",
        body: payload,
      }),
    }),
    updateAccountAnalyticsElement: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/api/analytics-elements/${payload.id}`,
        method: "POST",
        body: { ...payload, id: undefined },
      }),
    }),
    getAnalyticsElementTypes: builder.query<AnalyticsElementType[], void>({
      query: () => `/api/analytics-element-types`,
      transformResponse: (response: { data: AnalyticsElementType[] }) =>
        response.data,
    }),
    getAnalyticsElementsForPlacedOrder: builder.query<
      AnalyticsElement[],
      string
    >({
      query: (orderId) => `/store/orders/${orderId}/analytics-elements`,
      transformResponse: (response: { data: AnalyticsElement[] }) =>
        response.data,
    }),
    getAdditionalCheckoutFields: builder.query<
      AdditionalCheckoutField[],
      string
    >({
      query: (account_id) => `/api/accounts/${account_id}/checkout-fields`,
      transformResponse: (response: { data: AdditionalCheckoutField[] }) =>
        response.data,
    }),
    createAdditionalCheckoutField: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/api/checkout-fields`,
        method: "POST",
        body: payload,
      }),
    }),
    updateAdditionalCheckoutField: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/api/checkout-fields/${payload.id}`,
        method: "POST",
        body: { ...payload, id: undefined },
      }),
    }),
    updateIsEnabledForCheckoutField: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/api/checkout-fields/${payload.id}/enabled`,
        method: "POST",
        body: { ...payload, id: undefined },
      }),
    }),
    getCheckoutFieldReport: builder.query<
      any[],
      { account_id: string; product_id: string }
    >({
      query: (payload) =>
        `/api/products/${payload.product_id}/checkout-field-values?account_id=${payload.account_id}`,
      transformResponse: (response: { data: AdditionalCheckoutField[] }) =>
        response.data,
    }),
    getCheckoutFieldReportCsv: builder.query<
      string,
      { account_id: string; product_id: string }
    >({
      query(payload) {
        return {
          url: `/api/products/${payload.product_id}/checkout-field-values?format=csv&account_id=${payload.account_id}`,
          responseHandler: async (response) =>
            window.URL.createObjectURL(await response.blob()),
          cache: "no-cache",
        };
      },
    }),
    postFeaturedReview: builder.mutation<
      any,
      { review_id: string; product_ids: string[] }
    >({
      query: (payload) => ({
        url: `/api/reviews/${payload.review_id}/feature`,
        method: "POST",
        body: { product_ids: payload.product_ids },
      }),
    }),
    getSeatPrices: builder.query<any[], string>({
      query: (variant_id) => `/store/variants/${variant_id}/seat-prices`,
      transformResponse: (response: { data: SeatPrice[] }) => response.data,
    }),
    getPriceBasedVariantPriceRange: builder.query<
      { from_amount: number; to_amount: number },
      { variant_id: string; currency_code: string }
    >({
      query: (payload) =>
        `/store/variants/${payload.variant_id}/price-ranges/${payload.currency_code}`,
      transformResponse: (response: {
        data: { from_amount: number; to_amount: number };
      }) => response.data,
    }),
    getBrands: builder.query<Brand[], string>({
      query: (account_id) => `/api/accounts/${account_id}/brands`,
      transformResponse: (response: { data: Brand[] }) => response.data,
    }),
    getBrand: builder.query<Brand, { account_id: string; brand_id: number }>({
      query: (payload) =>
        `/api/brands/${payload.brand_id}?account_id=${payload.account_id}`,
      transformResponse: (response: { data: Brand }) => response.data,
    }),
    getPage: builder.query<Page, { account_id: string; page_id: number }>({
      query: (payload) =>
        `/api/pages/${payload.page_id}?account_id=${payload.account_id}`,
      transformResponse: (response: { data: Page }) => response.data,
    }),
    getLabels: builder.query<Label[], string>({
      query: (account_id) => `/api/labels?account_id=${account_id}`,
      transformResponse: (response: { data: Label[] }) => response.data,
    }),
    createLabel: builder.mutation<Brand, { name: string; account_id: string }>({
      query: (payload) => ({
        url: `/api/labels`,
        method: "POST",
        body: payload,
      }),
    }),
    createBrand: builder.mutation<
      Brand,
      { name: string; slug: string; account_id: string }
    >({
      query: (payload) => ({
        url: `/api/brands?account_id=${payload.account_id}`,
        method: "POST",
        body: { ...payload, account_id: undefined },
      }),
    }),
    createPage: builder.mutation<Brand, { name: string; brand_id: string }>({
      query: (payload) => ({
        url: `/api/pages`,
        method: "POST",
        body: payload,
      }),
    }),
    findEvents: builder.query<
      Product[],
      {
        account_id?: string;
        platform_id?: number;
        slug?: string;
        type?: string;
        latitude?: number;
        longitude?: number;
      }
    >({
      query: (params) => ({
        url: `/store/events`,
        params,
      }),
      transformResponse: (response: { data: Product[] }) => response.data,
    }),
    getSlugEntityBySlug: builder.query<
      Participant | ProductCategory | Venue | City,
      string
    >({
      query: (slug) => `/store/slugs/${slug}`,
      transformResponse: (response: {
        data: Participant | ProductCategory | Venue | City;
      }) => response.data,
    }),
    getMailingListsByAccountId: builder.query<MailingList[], string>({
      query: (account_id) => `/api/accounts/${account_id}/mailing-lists`,
      transformResponse: (response: { data: MailingList[] }) => response.data,
    }),
    getPassEligibleEventsForOrder: builder.query<PassesInfo, string>({
      query: (order_id) =>
        `/store/orders/${order_id}/reservation-eligible-events`,
      transformResponse: (response: { data: PassesInfo }) => response.data,
    }),
    updatePageEditorContent: builder.mutation<
      Brand,
      { page_id: number; editor_content: string }
    >({
      query: (payload) => ({
        url: `/api/pages/${payload.page_id}/editor-content`,
        method: "POST",
        body: { ...payload, page_id: undefined },
      }),
    }),
    checkInOrder: builder.mutation<
      { status: string },
      { ticket_id: string; scanned_device_id: string }
    >({
      query: (payload) => ({
        url: `/api/tickets/${payload.ticket_id}/check-in-order`,
        method: "POST",
        body: { ...payload, ticket_id: undefined },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    assignLabels: builder.mutation<
      { status: string },
      { subscription_ids: string[]; label_ids: number[] }
    >({
      query: (payload) => ({
        url: `/api/labels/assign`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    getSubscriber: builder.query<Subscriber, string>({
      query: (subscriberId) => ({ url: `/api/subscribers/${subscriberId}` }),
      transformResponse: (response: { data: Subscriber }) => response.data,
    }),
    removeLabel: builder.mutation<
      { status: string },
      { subscription_id: string; label_id: number[] }
    >({
      query: (payload) => ({
        url: `/api/labels/remove`,
        method: "DELETE",
        body: payload,
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    getCampaignFromAmplify: builder.query<Campaign, string>({
      query: (campaign_id) => ({ url: `/api/campaigns/${campaign_id}` }),
      transformResponse: (response: { data: Campaign }) => response.data,
    }),
    getCampaigns: builder.query<
      { data: Campaign[]; metadata: PaginationResponse },
      { account_id: string; status?: string } & PaginationRequest
    >({
      query: (params) => ({
        url: `/api/campaigns`,
        params: { ...params },
      }),
    }),
    updateCampaignAmplify: builder.mutation<
      { status: string },
      {
        campaign_id: string;
        included_variant_ids: string[];
        included_label_ids: number[];
        excluded_label_ids: number[];
      }
    >({
      query: (payload) => ({
        url: `/api/campaigns/${payload.campaign_id}`,
        method: "POST",
        body: { ...payload, campaign_id: undefined },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    updateIndividualAccountSettings: builder.mutation<
      void,
      {
        value: string;
        name: string;
        account_id: string;
      }
    >({
      query: (payload) => ({
        url: `/api/accounts/${payload.account_id}/settings`,
        method: "POST",
        body: { name: payload.name, value: payload.value },
      }),
    }),
    getBrandByDomain: builder.query<{ status: string; data?: Brand }, string>({
      query: (domain) => ({ url: `/store/brands/${domain}` }),
    }),
    emailPassLink: builder.mutation<
      { status: string },
      {
        order_display_id: string;
        email: string;
      }
    >({
      query: (payload) => ({
        url: `/store/passes/send-link`,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    unsubscribeSubscriptions: builder.mutation<{ status: string }, string[]>({
      query: (subscription_ids) => ({
        url: `/api/subscriptions/unsubscribe`,
        method: "POST",
        body: { subscription_ids },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    updateBrandSetting: builder.mutation<
      { status: string },
      { brand_id: number; name: string; value: string }
    >({
      query: (payload) => ({
        url: `/api/brands/${payload.brand_id}/settings`,
        method: "POST",
        body: { ...payload, brand_id: undefined },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    updateVenueSetting: builder.mutation<
      { status: string },
      { venue_id: string; name: string; value: string }
    >({
      query: (payload) => ({
        url: `/api/venues/${payload.venue_id}/settings`,
        method: "POST",
        body: { ...payload, venue_id: undefined },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    updateBrandThemeSetting: builder.mutation<
      { status: string },
      { brand_id: number; name: string; value: string }
    >({
      query: (payload) => ({
        url: `/api/brands/${payload.brand_id}/settings/theme`,
        method: "POST",
        body: { ...payload, brand_id: undefined },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    getDomainConfig: builder.query<DomainConfig, string>({
      query: (domain) => ({ url: `/api/domains/${domain}/config` }),
      transformResponse: (response: { data: DomainConfig }) => response.data,
    }),
    redeemTicket: builder.mutation<
      { status: string },
      { variant_id: string; redeemed_by: string; code: string }
    >({
      query: (payload) => ({
        url: `/store/tickets/redeem`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    createMenu: builder.mutation<void, CreateMenuRequest>({
      query: ({ brand_id, menu_name }) => ({
        url: "/api/menus",
        method: "POST",
        body: { brand_id, menu_name },
      }),
    }),
    createMenuItem: builder.mutation<void, any>({
      query: (payload) => ({
        url: "/api/menu-items",
        method: "POST",
        body: payload,
      }),
    }),
    updateMenuItem: builder.mutation<void, any>({
      query: (payload) => ({
        url: `/api/menu-items/${payload.menu_item_id}`,
        method: "POST",
        body: { ...payload, menu_item_id: undefined },
      }),
    }),
    reorderMenuItem: builder.mutation<
      void,
      { menu_item_id: number; direction: "up" | "down" }
    >({
      query: (payload) => ({
        url: `/api/menu-items/${payload.menu_item_id}/reorder`,
        method: "POST",
        body: { direction: payload.direction },
      }),
    }),
    deleteMenuItem: builder.mutation<void, string>({
      query: (menu_item_id) => ({
        url: `/api/menu-items/${menu_item_id}`,
        method: "DELETE",
      }),
    }),
    deleteMenu: builder.mutation<void, string>({
      query: (menu_id) => ({
        url: `/api/menus/${menu_id}`,
        method: "DELETE",
      }),
    }),
    getMenusByBrand: builder.query<{ data: Menu[] }, number>({
      query: (brand_id) => `/api/brands/${brand_id}/menus`,
    }),
    getMenuItemsByMenu: builder.query<{ data: MenuItem[] }, string>({
      query: (menu_id) => `/api/menus/${menu_id}/items`,
    }),
    getPrizes: builder.query<Prize[], string>({
      query: (contest_id) => ({
        url: `/api/contests/${contest_id}/prizes`,
      }),
      transformResponse: (response: { data: Prize[] }) => response.data,
    }),
    createPrize: builder.mutation<Prize, CreatePrizeRequest>({
      query: (payload) => ({
        url: `/api/contests/${payload.contest_id}/prizes`,
        method: "POST",
        body: { ...payload, contest_id: undefined },
      }),
    }),
    updatePrize: builder.mutation<Prize, UpdatePrizeRequest>({
      query: (payload) => ({
        url: `/api/prizes/${payload.id}`,
        method: "POST",
        body: { ...payload, id: undefined },
      }),
    }),
    deletePrize: builder.mutation<{ data: string }, string>({
      query: (prize_id) => ({
        url: `/api/prizes/${prize_id}`,
        method: "DELETE",
      }),
    }),
    getContest: builder.query<Contest, number>({
      query: (id) => ({ url: `/api/contests/${id}` }),
      transformResponse: (response: { data: Contest }) => response.data,
    }),
    getContestByKey: builder.query<Contest, string>({
      query: (key) => ({ url: `/store/contests/key/${key}` }),
      transformResponse: (response: { data: Contest }) => response.data,
    }),
    createContest: builder.mutation<Contest, CreateContestRequest>({
      query: (payload) => ({
        url: `/api/contests`,
        method: "POST",
        body: payload,
        transformResponse: (response: { data: Contest }) => response.data,
      }),
    }),
    getGames: builder.query<Game[], void>({
      query: () => ({ url: `/api/games` }),
      transformResponse: (response: { data: Game[] }) => response.data,
    }),
    getGame: builder.query<Game, number>({
      query: (game_id) => ({ url: `/api/games/${game_id}` }),
      transformResponse: (response: { data: Game }) => response.data,
    }),
    getContests: builder.query<Contest[], string>({
      query: (account_id) => ({ url: `/api/contests`, params: { account_id } }),
      transformResponse: (response: { data: Contest[] }) => response.data,
    }),
    updateContest: builder.mutation<Contest, UpdateContestRequest>({
      query: (payload) => ({
        url: `/api/contests/${payload.id}`,
        method: "POST",
        body: { ...payload, id: undefined },
        transformResponse: (response: { data: Contest }) => response.data,
      }),
    }),
    deleteContest: builder.mutation<{ data: string }, number>({
      query: (contest_id) => ({
        url: `/api/contests/${contest_id}`,
        method: "DELETE",
      }),
    }),
    createContestEntry: builder.mutation<
      ContestEntry,
      CreateContestEntryRequest
    >({
      query: (payload) => ({
        url: `/store/contest-entries`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: ContestEntry }) => response.data,
    }),
    getContestEntries: builder.query<
      { data: ContestEntry[]; metadata: PaginationResponse },
      {
        contest_id: number;
        show_winners_only: boolean;
      } & PaginationRequest
    >({
      query: (params) => ({
        url: `/api/contests/${params.contest_id}/contest-entries`,
        params: {
          ..._.omitBy(params, (item) => item === "" || item === null),
          contest_id: undefined,
        },
      }),
    }),
    completeContestEntry: builder.mutation<{ status: string }, string>({
      query: (contest_entry_id) => ({
        url: `/store/contest-entries/${contest_entry_id}/complete`,
        method: "POST",
      }),
    }),
    getVirtualVenues: builder.query<Venue[], void>({
      query: () => ({ url: `/api/venues?is_virtual=true` }),
      transformResponse: (response: { data: Venue[] }) => response.data,
    }),
    getAllVenues: builder.query<Venue[], void>({
      query: () => ({ url: `/api/venues` }),
      transformResponse: (response: { data: Venue[] }) => response.data,
    }),
    getLatestReviews: builder.query<Review[], GetReviewsRequest>({
      query: (params) => ({ url: `/api/reviews/latest`, params }),
      transformResponse: (response: { data: Review[] }) => response.data,
    }),
    filterReviews: builder.query<
      { data: Review[]; metadata: PaginationResponse },
      ReviewsFilterParams & PaginationRequest
    >({
      query: (params) => ({ url: `/api/reviews`, params }),
    }),
    getReferralLinks: builder.query<ReferralLink[], string>({
      query: (account_id) => ({
        url: `/api/referral-links`,
        params: { account_id },
      }),
      transformResponse: (response: { data: ReferralLink[] }) => response.data,
    }),
    createReferralLink: builder.mutation<
      { status: string },
      CreateReferralLinkRequest
    >({
      query: (payload) => ({
        url: `/api/referral-links`,
        method: "POST",
        body: payload,
      }),
    }),
    deleteReferralLink: builder.mutation<{ status: string }, number>({
      query: (id) => ({
        url: `/api/referral-links/${id}`,
        method: "DELETE",
      }),
    }),
    updateReferralLink: builder.mutation<
      { status: string },
      UpdateReferralLinkRequest
    >({
      query: (payload) => ({
        url: `/api/referral-links/${payload.id}`,
        method: "PUT",
        body: { ...payload, id: undefined },
      }),
    }),
    getTicketsForVariant: builder.query<any, string>({
      query: (variantId) => `/api/variants/${variantId}/tickets`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getPublicTicketsForVariant: builder.query<
      any,
      { variantId: string; encryptedProductId: string }
    >({
      query: (payload) =>
        `/store/variants/${payload.variantId}/tickets?encrypted_product_id=${payload.encryptedProductId}`,
      transformResponse: (response: { data: any[] }) => response.data,
    }),
    getReviewsSummary: builder.query<
      ReviewAnalyzerResults,
      { product_id?: string; account_id: string }
    >({
      query: (params) => ({ url: `/api/reviews/summary`, params }),
      transformResponse: (response: { data: ReviewAnalyzerResults }) =>
        response.data,
    }),
    deleteAutomation: builder.mutation<{ status: string }, string>({
      query: (id) => ({
        url: `/api/automations/${id}`,
        method: "DELETE",
      }),
    }),
    getPasses: builder.query<Product[], { account_id?: string }>({
      query: (params) => ({ url: `/store/passes`, params }),
      transformResponse: (response: { data: Product[] }) => response.data,
    }),
    getGiftCards: builder.query<Product[], { account_id?: string }>({
      query: (params) => ({ url: `/store/gift-cards`, params }),
      transformResponse: (response: { data: Product[] }) => response.data,
    }),
    getGiftCardsByOrderId: builder.query<
      PurchasedGiftCard[],
      { order_id: string }
    >({
      query: (order_id) => ({ url: `/store/gift-cards/order/${order_id}` }),
      transformResponse: (response: { data: PurchasedGiftCard[] }) =>
        response.data,
    }),
    sendGiftCard: builder.mutation<string, SendGiftCardRequest>({
      query: (payload) => ({
        url: `/store/gift-cards/${payload.gift_card_id}/send`,
        method: "POST",
        body: { ...payload, gift_card_id: undefined },
      }),
      transformResponse: (response: { data: string }) => response.data,
    }),
    getAudienceSize: builder.mutation<number, AudienceSegment[]>({
      query: (payload) => ({
        url: `/api/audiences/size`,
        method: "POST",
        body: { audience_segments: payload },
      }),
      transformResponse: (response: { data: number }) => response.data,
    }),
    updatePageSetting: builder.mutation<
      { status: string },
      { page_id: number; name: string; value: string }
    >({
      query: (payload) => ({
        url: `/api/pages/${payload.page_id}/settings`,
        method: "POST",
        body: { ...payload, page_id: undefined },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    deletePage: builder.mutation<{ status: string }, { page_id: number }>({
      query: (payload) => ({
        url: `/api/pages/${payload.page_id}`,
        method: "DELETE",
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    sortPage: builder.mutation<
      { status: string },
      { page_id: number; other_page_id: number }
    >({
      query: (payload) => ({
        url: `/api/pages/${payload.page_id}/sort`,
        method: "POST",
        body: { other_page_id: payload.other_page_id },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    updateParticipantSetting: builder.mutation<
      { status: string },
      { participant_id: string; name: string; value: string }
    >({
      query: (payload) => ({
        url: `/api/participants/${payload.participant_id}/settings`,
        method: "POST",
        body: { ...payload, participant_id: undefined },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    updateParticipantSocialMediaUrlContentBlock: builder.mutation<
      { status: string },
      UpdateParticipantSocialMediaUrlContentBlockRequest
    >({
      query: (payload) => ({
        url: `/api/participants/${payload.participant_id}/social-media-url-content-block`,
        method: "POST",
        body: { ...payload, participant_id: undefined },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    getAffiliateCodeByCode: builder.query<AffiliateCode, string>({
      query: (code) => ({ url: `/store/affiliate-codes/code/${code}` }),
      transformResponse: (response: { data: AffiliateCode }) => response.data,
    }),
    getWebAnalyticsForProduct: builder.query<
      any,
      { product_id: string; type: string }
    >({
      query: (payload) =>
        `/api/web-analytics/products/${payload.product_id}/${payload.type}`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    getSeatAllocationsByLineItemId: builder.query<SeatAllocation[], string>({
      query: (line_item_id) =>
        `/api/seat-allocations/line-items/${line_item_id}`,
      transformResponse: (response: { data: any }) => response.data,
    }),
    updateSeatSelections: builder.mutation<
      { status: string },
      UpdateSeatSelectionsRequest
    >({
      query: (payload) => ({
        url: `/api/seat-allocations/line-items/${payload.line_item_id}`,
        method: "POST",
        body: { ...payload, line_item_id: undefined },
      }),
      transformResponse: (response: { data: { status: string } }) =>
        response.data,
    }),
    unsubscribe: builder.mutation<
      any,
      { mailing_list_id: string; subscriber_id: string; campaign_id: string }
    >({
      query: (payload) => ({
        url: `/store/mailing-lists/${payload.mailing_list_id}/unsubscribe`,
        method: "POST",
        body: {
          subscriber_id: payload.subscriber_id,
          campaign_id: payload.campaign_id,
        },
      }),
    }),
    getSubscribers: builder.query<
      { data: Subscription[]; metadata: PaginationResponse },
      { mailing_list_id: string; query: string } & PaginationRequest
    >({
      query: (params) => ({
        url: `/api/mailing-lists/${params.mailing_list_id}/subscriptions`,
        params: { ...params, mailing_list_id: undefined },
      }),
    }),
    downloadSubscribersByMailingListId: builder.query<string, string>({
      query(mailing_list_id) {
        return {
          url: `/api/mailing-lists/${mailing_list_id}/subscriptions/download`,
          responseHandler: async (response) =>
            window.URL.createObjectURL(await response.blob()),
          cache: "no-cache",
        };
      },
    }),
    getProductsToManage: builder.query<
      { data: Product[]; metadata: PaginationResponse },
      {
        account_id: string;
        filter_type: EventListFilterTypes;
      } & PaginationRequest
    >({
      query: (input) => ({
        url: "/api/events",
        params: input,
      }),
    }),
    updateOrderNotes: builder.mutation<
      void,
      { order_id: string; notes: string }
    >({
      query: (payload) => ({
        url: `/api/orders/${payload.order_id}/notes`,
        method: "POST",
        body: { notes: payload.notes },
      }),
    }),
    adminAssignTickets: builder.mutation<
      void,
      { ticket_ids: []; assigned_to: string }
    >({
      query: (payload) => ({
        url: `/api/tickets/assign`,
        method: "POST",
        body: payload,
      }),
    }),
    createMailingListWelcomeEmail: builder.mutation<void, string>({
      query: (mailing_list_id) => ({
        url: `/api/mailing-lists/${mailing_list_id}/welcome-email`,
        method: "POST",
      }),
    }),
    updateEmailSetting: builder.mutation<
      void,
      {
        email_id: number;
        value: string;
        name: string;
        account_id: string;
      }
    >({
      query: (payload) => ({
        url: `/api/emails/${payload.email_id}/settings`,
        method: "POST",
        body: { ...payload, email_id: undefined },
      }),
    }),
    updateEmailContent: builder.mutation<
      void,
      {
        email_id: number;
        content: string;
        body: string;
        account_id: string;
      }
    >({
      query: (payload) => ({
        url: `/api/emails/${payload.email_id}/content`,
        method: "POST",
        body: { ...payload, email_id: undefined },
      }),
    }),
    sendTestEmail: builder.mutation<
      void,
      {
        email_id: number;
        email: string;
        account_id: string;
      }
    >({
      query: (payload) => ({
        url: `/api/emails/${payload.email_id}/send-test-email`,
        method: "POST",
        body: { ...payload, email_id: undefined },
      }),
    }),
    addSubscriberToMailingList: builder.mutation<
      void,
      MailingListSubscriberValues
    >({
      query: (payload) => ({
        url: `/store/subscriptions`,
        method: "POST",
        body: payload,
      }),
    }),
    getTickets: builder.query<
      Ticket[],
      { productId?: string; variantId?: string; orderId?: string }
    >({
      query: (params) => ({
        url: `/api/tickets`,
        params: {
          product_id: params.productId,
          variant_id: params.variantId,
          order_id: params.orderId,
        },
      }),
      transformResponse: (response: { data: Ticket[] }) => response.data,
    }),
    checkInTicket: builder.mutation<
      { result: string; ticket?: Ticket },
      { ticket_id: string; device_id: string }
    >({
      query: (payload) => ({
        url: `/api/tickets/${payload.ticket_id}/check-in`,
        method: "POST",
        body: { device_id: payload.device_id },
      }),
      transformResponse: (response: {
        data: { result: string; ticket?: Ticket };
      }) => response.data,
    }),
    undoCheckInTicket: builder.mutation<{ status: string }, string>({
      query: (ticket_id) => ({
        url: `/api/tickets/${ticket_id}/undo-check-in`,
        method: "POST",
      }),
    }),
    createProductAvailableNotification: builder.mutation<
      void,
      NotifyWhenProductAvailableValues
    >({
      query: (payload) => ({
        url: `/store/notifications/product-available-for-sale`,
        method: "POST",
        body: payload,
      }),
    }),
    getMailingListSummaries: builder.query<MailingListSummary[], string>({
      query: (mailing_list_id) => ({
        url: `/api/mailing-lists/${mailing_list_id}/summaries`,
      }),
      transformResponse: (response: { data: MailingListSummary[] }) =>
        response.data,
    }),
    getSummaryTypes: builder.query<SummaryType[], void>({
      query: () => ({
        url: `/api/summary-types`,
      }),
      transformResponse: (response: { data: SummaryType[] }) => response.data,
    }),
    createMailingListSummary: builder.mutation<
      void,
      { send_at: Date; mailing_list_id: string; summary_type_id: number }
    >({
      query: (payload) => ({
        url: `/api/mailing-list-summaries`,
        method: "POST",
        body: payload,
      }),
    }),
    updateMailingListSummary: builder.mutation<
      void,
      {
        send_at: Date;
        mailing_list_summary_id: string;
        summary_type_id: number;
      }
    >({
      query: (payload) => ({
        url: `/api/mailing-list-summaries/${payload.mailing_list_summary_id}`,
        method: "POST",
        body: { ...payload, mailing_list_summary_id: undefined },
      }),
    }),
    deleteMailingListSummary: builder.mutation<void, number>({
      query: (mailing_list_summary_id) => ({
        url: `/api/mailing-list-summaries/${mailing_list_summary_id}`,
        method: "DELETE",
      }),
    }),
    createTicketInvite: builder.mutation<{ code: string }, CreateLinkValues>({
      query: (payload) => ({
        url: `/api/ticket-invites`,
        method: "POST",
        body: payload,
      }),
    }),
    updateMailingListSetting: builder.mutation<
      { code: string },
      {
        account_id: string;
        mailing_list_id: string;
        name: string;
        value: any;
      }
    >({
      query: (payload) => ({
        url: `/api/mailing-lists/${payload.mailing_list_id}/settings`,
        method: "POST",
        body: { ...payload, mailing_list_id: undefined },
      }),
    }),
    getProductAvailableForSaleSubscriptions: builder.query<
      ProductAvailableForSaleSubscription[],
      string
    >({
      query: (product_id) => ({
        url: `/api/notifications/product-available-for-sale`,
        params: { product_id },
      }),
      transformResponse: (response: {
        data: ProductAvailableForSaleSubscription[];
      }) => response.data,
    }),
    getProductAvailableForSaleSubscriptionsSummary: builder.query<
      ProductAvailableForSaleSubscriptionSummaryRow[],
      string
    >({
      query: (account_id) => ({
        url: `/api/notifications/product-available-for-sale/summary`,
        params: { account_id },
      }),
      transformResponse: (response: {
        data: ProductAvailableForSaleSubscriptionSummaryRow[];
      }) => response.data,
    }),
    getTicketSummary: builder.query<TicketSummary[], { product_id: string }>({
      query: (params) => ({
        url: `/api/tickets/summary`,
        params,
      }),
      transformResponse: (response: { data: TicketSummary[] }) => response.data,
    }),
    deleteTemplate: builder.mutation<{ code: string }, string>({
      query: (template_id) => ({
        url: `/api/templates/${template_id}`,
        method: "DELETE",
      }),
    }),
    createTemplate: builder.mutation<void, CreateTemplateRequest>({
      query: (payload) => ({
        url: `/api/templates`,
        method: "POST",
        body: payload,
      }),
    }),
    getTemplates: builder.query<
      Template[],
      { type_identifier: string; account_id: string }
    >({
      query: (params) => ({ url: `/api/templates`, params }),
      transformResponse: (response: { data: Template[] }) => response.data,
    }),
    updateCampaignEmailContent: builder.mutation<
      void,
      { campaign_id: string; body: string; editor_content: string }
    >({
      query: (payload) => ({
        url: `/api/campaigns/${payload.campaign_id}/email-content`,
        method: "POST",
        body: { ...payload, campaign_id: undefined },
      }),
    }),
    createContestDiscountCode: builder.mutation<
      void,
      SaveContestDiscountCodeRequest
    >({
      query: (payload) => ({
        url: `/api/contest-discount-codes`,
        method: "POST",
        body: payload,
      }),
    }),
    updateContestDiscountCode: builder.mutation<
      void,
      SaveContestDiscountCodeRequest
    >({
      query: (payload) => ({
        url: `/api/contest-discount-codes/${payload.id}`,
        method: "POST",
        body: { ...payload, id: undefined },
      }),
    }),
    getContestDiscountCode: builder.query<ContestDiscountCode, number>({
      query: (contest_discount_code_id) => ({
        url: `/api/contest-discount-codes/${contest_discount_code_id}`,
      }),
      transformResponse: (response: { data: ContestDiscountCode }) =>
        response.data,
    }),
    updateContestDiscountCodeSetting: builder.mutation<
      void,
      { name: string; value: any; contest_discount_code_id: number }
    >({
      query: (payload) => ({
        url: `/api/contest-discount-codes/${payload.contest_discount_code_id}/settings`,
        method: "POST",
        body: { ...payload, contest_discount_id: undefined },
      }),
    }),
    deleteContestDiscountCode: builder.mutation<void, number>({
      query: (contest_discount_code_id) => ({
        url: `/api/contest-discount-codes/${contest_discount_code_id}`,
        method: "DELETE",
      }),
    }),
    createContestDiscountCodesEmail: builder.mutation<void, number>({
      query: (contest_id) => ({
        url: `/api/contests/${contest_id}/discount-codes-email`,
        method: "POST",
      }),
    }),
    getTipaltiIframeUrl: builder.mutation<
      string,
      { payment_destination_id: string; page: string }
    >({
      query: (payload) => ({
        url: `/api/payments/tipalti/iframe-url`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: string }) => response.data,
    }),
    createTipaltiPayee: builder.mutation<string, CreateTipaltiPayeeRequest>({
      query: (payload) => ({
        url: `/api/payments/tipalti/payees`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: string }) => response.data,
    }),
    getPaymentDestinations: builder.query<PaymentDestination[], string>({
      query: (account_id) => ({
        url: `/api/payment-destinations?account_id=${account_id}`,
      }),
      transformResponse: (response: { data: PaymentDestination[] }) =>
        response.data,
    }),
    createProductContentBlock: builder.mutation<
      string,
      SaveProductContentBlockRequest
    >({
      query: (payload) => ({
        url: `/api/products/${payload.product_id}/content-blocks`,
        method: "POST",
        body: { ...payload, product_id: undefined },
      }),
      transformResponse: (response: { data: string }) => response.data,
    }),
    deleteProductContentBlock: builder.mutation<void, number>({
      query: (product_content_block_id) => ({
        url: `/api/product-content-blocks/${product_content_block_id}`,
        method: "DELETE",
      }),
    }),
    updateProductContentBlock: builder.mutation<
      void,
      SaveProductContentBlockRequest
    >({
      query: (payload) => ({
        url: `/api/product-content-blocks/${payload.product_content_block_id}`,
        method: "POST",
        body: { ...payload, product_content_block_id: undefined },
      }),
    }),
    getProductContentBlocks: builder.query<
      ProductContentBlock[],
      { product_id: string; type: string }
    >({
      query: (payload) => ({
        url: `/api/products/${payload.product_id}/content-blocks`,
        params: { ...payload, product_id: undefined },
      }),
      transformResponse: (response: { data: ProductContentBlock[] }) =>
        response.data,
    }),
    getVenue: builder.query<Venue, string>({
      query: (venue_id) => ({
        url: `/api/venues/${venue_id}`,
      }),
      transformResponse: (response: { data: Venue }) => response.data,
    }),
    getAccountVenues: builder.query<Venue[], string>({
      query: (account_id) => ({
        url: `/api/venues`,
        params: { account_id },
      }),
      transformResponse: (response: { data: Venue[] }) => response.data,
    }),
    getVenueTicketStats: builder.query<any, string>({
      query: (venue_id) => ({
        url: `/api/venues/${venue_id}/ticket-stats`,
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    createWidget: builder.mutation<Widget, CreateWidgetRequest>({
      query: (payload) => ({
        url: `/api/widgets`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: Widget }) => response.data,
    }),

    deleteWidget: builder.mutation<void, number>({
      query: (widget_id) => ({
        url: `/api/widgets/${widget_id}`,
        method: "DELETE",
      }),
    }),

    updateWidget: builder.mutation<void, SaveWidgetRequest>({
      query: (payload) => ({
        url: `/api/widgets/${payload.widget_id}`,
        method: "POST",
        body: { ...payload, widget_id: undefined },
      }),
    }),

    getWidget: builder.query<Widget, number>({
      query: (widget_id) => ({
        url: `/api/widgets/${widget_id}`,
        method: "GET",
      }),
      transformResponse: (response: { data: Widget }) => response.data,
    }),

    getWidgets: builder.query<Widget[], { account_id: string }>({
      query: (payload) => ({
        url: `/api/widgets`,
        params: { account_id: payload.account_id },
      }),
      transformResponse: (response: { data: Widget[] }) => response.data,
    }),
    publishWidget: builder.mutation<void, number>({
      query: (widget_id) => ({
        url: `/api/widgets/${widget_id}/publish`,
        method: "POST",
      }),
    }),
    resetWidget: builder.mutation<void, number>({
      query: (widget_id) => ({
        url: `/api/widgets/${widget_id}/reset`,
        method: "POST",
      }),
    }),
    checkEmail: builder.mutation<CheckEmailResult, string>({
      query: (email) => ({
        url: `/store/check-email`,
        method: "POST",
        body: { email },
      }),
      transformResponse: (response: { data: CheckEmailResult }) =>
        response.data,
    }),
    isPaymentMigrationRequired: builder.query<boolean, string>({
      query: (account_id) => ({
        url: `/api/accounts/${account_id}/is-payment-migration-required`,
      }),
      transformResponse: (response: { data: boolean }) => response.data,
    }),
    getProductPaymentDestinations: builder.query<
      ProductPaymentDestinations,
      string
    >({
      query: (product_id) => ({
        url: `/api/products/${product_id}/payment-destinations`,
      }),
      transformResponse: (response: { data: ProductPaymentDestinations }) =>
        response.data,
    }),
    updatePaymentDestination: builder.mutation<
      any,
      UpdatePaymentDestinationRequest
    >({
      query: (params) => ({
        url: "/api/update-payment_destination",
        method: "POST",
        body: params,
      }),
    }),
    getLiveViewComponentPreview: builder.mutation<{ data: string }, any>({
      query: (payload) => ({
        url: `/store/page-builder/preview`,
        method: "POST",
        body: payload,
      }),
    }),
    getPermissions: builder.query<Permission[], void>({
      query: (accountId) => ({ url: `/api/permissions` }),
      transformResponse: (response: { data: Permission[] }) => response.data,
    }),
  }),
});

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
  useGetPermissionsQuery,
  useGetLiveViewComponentPreviewMutation,
  useUpdatePaymentDestinationMutation,
  useLazyGetPaymentDestinationsQuery,
  useLazyIsPaymentMigrationRequiredQuery,
  useCheckEmailMutation,
  useCreateTipaltiPayeeMutation,
  usePublishWidgetMutation,
  useResetWidgetMutation,
  useCreateWidgetMutation,
  useDeleteWidgetMutation,
  useUpdateWidgetMutation,
  useGetWidgetQuery,
  useLazyGetWidgetsQuery,
  useGetVenueTicketStatsQuery,
  useLazyGetAccountVenuesQuery,
  useGetVenueQuery,
  useUpdateProductContentBlockMutation,
  useDeleteProductContentBlockMutation,
  useGetProductContentBlocksQuery,
  useCreateProductContentBlockMutation,
  useGetTipaltiIframeUrlMutation,
  useDeleteContestDiscountCodeMutation,
  useCreateContestDiscountCodesEmailMutation,
  useUpdateContestDiscountCodeSettingMutation,
  useUpdateContestDiscountCodeMutation,
  useLazyGetContestDiscountCodeQuery,
  useCreateContestDiscountCodeMutation,
  useUpdateCampaignEmailContentMutation,
  useLazyGetTemplatesQuery,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useGetTicketSummaryQuery,
  useLazyGetProductAvailableForSaleSubscriptionsSummaryQuery,
  useGetProductAvailableForSaleSubscriptionsQuery,
  useUpdateMailingListSettingMutation,
  useCreateTicketInviteMutation,
  useDeleteMailingListSummaryMutation,
  useUpdateMailingListSummaryMutation,
  useCreateMailingListSummaryMutation,
  useGetSummaryTypesQuery,
  useGetMailingListSummariesQuery,
  useCreateProductAvailableNotificationMutation,
  useUndoCheckInTicketMutation,
  useCheckInTicketMutation,
  useLazyGetTicketsQuery,
  useGetTicketsQuery,
  useAddSubscriberToMailingListMutation,
  useSendTestEmailMutation,
  useUpdateEmailContentMutation,
  useUpdateEmailSettingMutation,
  useCreateMailingListWelcomeEmailMutation,
  useAdminAssignTicketsMutation,
  useUpdateOrderNotesMutation,
  useLazyGetProductsToManageQuery,
  useLazyDownloadSubscribersByMailingListIdQuery,
  useGetSubscribersQuery,
  useLazyGetCampaignsQuery,
  useDeletePrizeMutation,
  useUnsubscribeMutation,
  useUpdateSeatSelectionsMutation,
  useGetSeatAllocationsByLineItemIdQuery,
  useLazyGetWebAnalyticsForProductQuery,
  useUpdateVenueSettingMutation,
  useCloneEmailCampaignMutation,
  useCloneMetaCampaignMutation,
  useGetAffiliateCodeByCodeQuery,
  useUpdateParticipantSocialMediaUrlContentBlockMutation,
  useSendGiftCardMutation,
  useGetGiftCardsByOrderIdQuery,
  useUpdateParticipantSettingMutation,
  useLazyGetCheckoutFieldReportCsvQuery,
  useSortPageMutation,
  useDeletePageMutation,
  useUpdatePageSettingMutation,
  useGetAudienceSizeMutation,
  useUpdateBrandThemeSettingMutation,
  useGetGiftCardsQuery,
  useGetPassesQuery,
  useDeleteContestMutation,
  useDeleteAutomationMutation,
  useLazyGetReviewsSummaryQuery,
  useLazyGetPublicTicketsForVariantQuery,
  useLazyGetTicketsForVariantQuery,
  useUpdateReferralLinkMutation,
  useDeleteReferralLinkMutation,
  useCreateReferralLinkMutation,
  useLazyGetReferralLinksQuery,
  useLazyFilterReviewsQuery,
  useGetContestByKeyQuery,
  useGetGameQuery,
  useLazyGetLatestReviewsQuery,
  useGetAllVenuesQuery,
  useReorderMenuItemMutation,
  useGetVirtualVenuesQuery,
  useCompleteContestEntryMutation,
  useLazyGetContestEntriesQuery,
  useCreateContestEntryMutation,
  useUpdateContestMutation,
  useLazyGetContestsQuery,
  useGetGamesQuery,
  useCreateContestMutation,
  useGetContestQuery,
  useUpdatePrizeMutation,
  useCreatePrizeMutation,
  useLazyGetPrizesQuery,
  useCreateMenuMutation,
  useGetMenuItemsByMenuQuery,
  useCreateMenuItemMutation,
  useUpdateMenuItemMutation,
  useDeleteMenuItemMutation,
  useDeleteMenuMutation,
  useGetMenusByBrandQuery,
  useUpdateIndividualAccountSettingsMutation,
  useRedeemTicketMutation,
  useLazyGetDomainConfigQuery,
  useUpdateBrandSettingMutation,
  useUnsubscribeSubscriptionsMutation,
  useEmailPassLinkMutation,
  useGetBrandByDomainQuery,
  useUpdateCampaignAmplifyMutation,
  useLazyGetCampaignFromAmplifyQuery,
  useRemoveLabelMutation,
  useLazyGetSubscriberQuery,
  useAssignLabelsMutation,
  useCreateLabelMutation,
  useLazyGetLabelsQuery,
  useCreatePageMutation,
  useGetPassEligibleEventsForOrderQuery,
  useCheckInOrderMutation,
  useGetSlugEntityBySlugQuery,
  useLazyGetMailingListsByAccountIdQuery,
  useUpdatePageEditorContentMutation,
  useLazyFindEventsQuery,
  useLazyGetPageQuery,
  useLazyGetBrandQuery,
  useCreateBrandMutation,
  useLazyGetBrandsQuery,
  usePostFeaturedReviewMutation,
  useLazyGetPriceBasedVariantPriceRangeQuery,
  useLazyGetSeatPricesQuery,
  useLazyGetCheckoutFieldReportQuery,
  useUpdateIsEnabledForCheckoutFieldMutation,
  useUpdateAdditionalCheckoutFieldMutation,
  useCreateAdditionalCheckoutFieldMutation,
  useLazyGetAdditionalCheckoutFieldsQuery,
  useGetAnalyticsElementsForPlacedOrderQuery,
  useUpdateAccountAnalyticsElementMutation,
  useCreateAccountAnalyticsElementMutation,
  useGetAnalyticsElementTypesQuery,
  useLazyGetAccountAnalyticsElementsQuery,
  useGetSignedCustomAudienceTosQuery,
  useCreateInstagramActorIdsByPageIdMutation,
  useLazyGetInstagramActorIdsByPageIdQuery,
  useUpdateMetaAdMutation,
  useUpdateMetaCampaignMutation,
  useUpdateMetaAdSetMutation,
  useDeleteMetaCampaignMutation,
  useGetMetaCampaignQuery,
  useGetMetaAdSetQuery,
  useCreateMetaAdSetMutation,
  useDeleteMetaAdMutation,
  useDeleteAdCreativeMutation,
  useGetMetaObjectStorySpecPreviewMutation,
  useLazyGetMetaAdCreativePreviewQuery,
  useGetCallToActionTypesQuery,
  useLazyGetMetaAdPreviewQuery,
  useCreateMetaAdMutation,
  useLazyGetMetaAdsInAdSetsQuery,
  useGetMetaCampaignAdSetsQuery,
  useGetAdPlatformCampaignQuery,
  useGetMetaCampaignObjectivesQuery,
  useCreateMetaCampaignMutation,
  useLazyGetMetaAdPixelsQuery,
  useCreateMetaAdCreativeMutation,
  useGetMetaPagesQuery,
  useLazyGetMetaPagesQuery,
  useLazyGetMetaAdCreativesQuery,
  useCreateCustomAudienceMutation,
  useGetMetaCustomAudiencesQuery,
  useGetMetaBudgetTypesQuery,
  useLazySearchMetaCitiesQuery,
  useLazySearchMetaInterestsQuery,
  useLazySearchMetaCountriesQuery,
  useGetMetaCountriesQuery,
  useLazyGetAdCampaignsQuery,
  useLazyGetAdPlatformAccountsQuery,
  useUpdateMetaAdAccountsMutation,
  useGetProductPaymentDestinationsQuery,
} = adsSlice;

export type UpdatePaymentDestinationRequest = {
  product_id?: string;
  variant_id?: string;
  account_id?: string;
  payment_destination_id: string;
};

export type CheckEmailResult = {
  corrected_email?: string;
  original_email: string;
};
export type CreateTipaltiPayeeRequest = {
  first_name?: string;
  last_name?: string;
  company_name?: string;
  entity_type: string;
  account_id: string;
};
export type SaveProductContentBlockRequest = {
  product_content_block_id?: number;
  product_id?: string;
  title: string;
  text: string;
  type: string;
};
export type SaveContestDiscountCodeRequest = {
  id?: number;
  contest_id: number;
  reason: string;
  name: string;
  quantity: number;
  prize_id?: number;
  discount_type: "fixed" | "percentage";
  discount_value: number;
  discount_allocation: string;
  discount_code_prefix: string;
  eligibility_rule: string;
  description: string;
  currency_code: string;
};

export type CreateWidgetRequest = {
  name: string;
  type: string;
  account_id?: string;
};

export type SaveWidgetRequest = {
  widget_id: number;
  draft_config: Record<string, any>;
};

interface CreateMenuRequest {
  brand_id: string;
  menu_name: string;
}

type PrizeBase = {
  name: string;
  winner_message: string;
  max_winners: number;
  win_probability: number;
  thumbnail?: string;
  max_winners_per_period?: number;
  period_duration_in_seconds?: number;
};

export type CreateTemplateRequest = {
  type_identifier: string;
  account_id: string;
  name: string;
  text: string;
  editor_content: string;
};

export type CreatePrizeRequest = PrizeBase & {
  contest_id: string;
};

export type UpdatePrizeRequest = PrizeBase & {
  id: number;
};

export type CreateContestRequest = {
  name: string;
  game_id: number;
  account_id: string;
};

export type UpdateContestRequest = {
  id: number;
  name: string;
  game_id: number;
  description: string;
  subscribed_lost_text: string;
  not_subscribed_lost_text: string;
  thumbnail?: string;
  marketing_opt_in_prompt?: string;
  terms?: string;
  entry_rule: string;
  game_config: Record<string, any>;
  lost_image?: string;
  rules?: string;
};

export type CreateContestEntryRequest = {
  location_info?: LocationResult;
  contest_id: number;
  name: "";
  email: "";
  subscribed: boolean;
  termsOfService: boolean;
};

export type GetReviewsRequest = {
  account_id?: string;
  count?: number;
  with_notes?: boolean;
};

export type CreateReferralLinkRequest = {
  account_id: string;
  code: string;
  name: string;
};

export type UpdateReferralLinkRequest = CreateReferralLinkRequest & {
  id: number;
};

export type SendGiftCardRequest = {
  email: string;
  email_subject: string;
  email_content: string;
  gift_card_id: string;
};

export type UpdateParticipantSocialMediaUrlContentBlockRequest = {
  participant_id: string;
  value: string;
  type: string;
};

export type UpdateSeatSelectionsRequest = {
  line_item_id: string;
  seat_ids: string[];
  seat_numbers: string[];
};

export type VariantPaymentDestination = {
  variant_id: string;
  variant_payment_destination: PaymentDestination;
  is_variant_payment_destination_at_account_level: boolean;
  is_variant_payment_destination_at_product_level: boolean;
  is_variant_payment_destination_at_variant_level: boolean;
};

export type ProductPaymentDestinations = {
  product_payment_destination: PaymentDestination;
  is_product_payment_destination_at_account_level: boolean;
  is_product_payment_destination_at_product_level: boolean;
  variant_payment_destinations: VariantPaymentDestination[];
};
